import React, { useState, useEffect, useCallback } from 'react';
import { getUsers, deleteUser, getColleges } from '../services/api';
import { FiUsers, FiSearch, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import EmptyState from '../components/EmptyState';
import ConfirmDialog from '../components/ConfirmDialog';

const UserManagement = ({ onLogout }) => {
  const userData = localStorage.getItem('user');
  const currentUser = userData ? JSON.parse(userData) : null;
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCollege, setSelectedCollege] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [deletingUser, setDeletingUser] = useState(null);

  const fetchUsersList = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedRole) params.role = selectedRole;
      if (selectedCollege) params.collegeId = selectedCollege;
      if (searchTerm) params.search = searchTerm;

      const data = await getUsers(params);
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users list');
    } finally {
      setLoading(false);
    }
  }, [selectedRole, selectedCollege, searchTerm]);

  useEffect(() => {
    fetchUsersList();
  }, [fetchUsersList]);

  useEffect(() => {
    getColleges().then(data => setColleges(data || [])).catch(console.error);
  }, []);

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      setActionLoading(deletingUser._id);
      setError(null);
      setSuccess(null);
      await deleteUser(deletingUser._id);
      setSuccess(`${deletingUser.role.toUpperCase()} account "${deletingUser.name}" deleted successfully.`);
      setDeletingUser(null);
      fetchUsersList();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <Sidebar onLogout={onLogout} />
        <div className="main-content">
          <div className="alert alert-error">
            <FiAlertCircle size={24} />
            <span>Access Denied: Only administrators can manage users.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar onLogout={onLogout} />
      <div className="main-content">
        <Header
          title="User Management"
          subtitle="View, filter, and manage registered Student and Teacher accounts across institutions."
        />

        {error && <div className="alert alert-error mb-4">{error}</div>}
        {success && <div className="alert alert-success mb-4">{success}</div>}

        {/* Filters and Search Toolbar */}
        <div className="card" style={{ padding: '16px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            
            {/* Search Input */}
            <div style={{ flex: '1 1 240px', position: 'relative' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                className="form-input"
                style={{ paddingLeft: '36px', width: '100%', height: '40px' }}
                placeholder="Search by user name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Role Filter */}
            <div style={{ minWidth: '160px', flex: '1 1 150px' }}>
              <select
                className="form-input"
                style={{ height: '40px' }}
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="">All Roles (Students & Teachers)</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
            </div>

            {/* College Filter */}
            <div style={{ minWidth: '180px', flex: '1 1 160px' }}>
              <select
                className="form-input"
                style={{ height: '40px' }}
                value={selectedCollege}
                onChange={(e) => setSelectedCollege(e.target.value)}
              >
                <option value="">All Colleges</option>
                {colleges.map(c => (
                  <option key={c._id} value={c._id}>{c.collegeName} ({c.collegeCode})</option>
                ))}
              </select>
            </div>

          </div>
        </div>

        {/* Users Table */}
        <div className="card">
          <div className="d-flex justify-between align-center mb-3">
            <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Registered Accounts ({users.length})</h3>
          </div>

          {loading ? (
            <div className="text-center p-5">
              <div className="spinner mx-auto"></div>
              <p className="mt-2 text-secondary">Loading user records...</p>
            </div>
          ) : users.length === 0 ? (
            <EmptyState
              icon={<FiUsers />}
              title="No accounts found"
              description="No user accounts match your current filter parameters."
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface-hover)' }}>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600' }}>User</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Email Address</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Role</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600' }}>College</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600' }}>Joined</th>
                    <th style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u._id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '600', color: 'var(--text-primary)' }}>{u.name}</td>
                      <td style={{ padding: '12px 16px' }}><code>{u.email}</code></td>
                      <td style={{ padding: '12px 16px' }}>
                        <span className={`badge badge-${u.role}`}>
                          {u.role.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {u.collegeId ? (
                          <span>{u.collegeId.collegeName} ({u.collegeId.collegeCode})</span>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>
                        {new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          style={{ color: 'var(--danger)' }}
                          onClick={() => setDeletingUser(u)}
                          disabled={actionLoading === u._id}
                        >
                          <FiTrash2 /> {actionLoading === u._id ? '...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(deletingUser)}
        title={`Delete ${deletingUser?.role?.toUpperCase()} Account?`}
        message={`Are you sure you want to delete user account "${deletingUser?.name}" (${deletingUser?.email})? This action cannot be undone.`}
        confirmText="Delete Account"
        onConfirm={handleConfirmDeleteUser}
        onCancel={() => setDeletingUser(null)}
      />
    </div>
  );
};

export default UserManagement;
